import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, appId } from '../../firebase-config';
import { collection, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { ClockIcon, ExclamationTriangleIcon, PlusIcon, TrashIcon, PencilIcon } from '../Icons';

const SuspensoesPrazo = ({ isOpen, onClose, suspensoes }) => {
  const userId = auth.currentUser?.uid;

  // Estados
  const [novaSuspensao, setNovaSuspensao] = useState({
    nome: '',
    dataInicio: '',
    dataFim: '',
    tipo: 'recesso',
    tribunal: '',
    observacoes: '',
    ativo: true
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [editandoSuspensao, setEditandoSuspensao] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());

  const handleOpenEdit = (suspensao) => {
    setEditandoSuspensao({
        ...suspensao,
        dataInicio: suspensao.dataInicio?.toISOString().split('T')[0] || '',
        dataFim: suspensao.dataFim?.toISOString().split('T')[0] || ''
    });
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editandoSuspensao) {
      handleEditSuspensao();
    } else {
      handleAddSuspensao();
    }
  };
  
  // Predefinições de suspensão
  const SUSPENSOES_PREDEFINIDAS = useMemo(() => [
    {
      nome: 'Recesso Forense - Dezembro/Janeiro',
      dataInicio: `${filtroAno}-12-20`,
      dataFim: `${filtroAno + 1}-01-06`,
      tipo: 'recesso',
      tribunal: 'Geral',
      observacoes: 'Suspensão automática dos prazos processuais conforme CNJ'
    },
    {
      nome: 'Recesso Forense - Julho',
      dataInicio: `${filtroAno}-07-01`,
      dataFim: `${filtroAno}-07-31`,
      tipo: 'recesso',
      tribunal: 'Geral',
      observacoes: 'Recesso de meio de ano'
    }
  ], [filtroAno]);

  // Adicionar nova suspensão
  const handleAddSuspensao = async () => {
    if (!novaSuspensao.nome.trim() || !novaSuspensao.dataInicio || !novaSuspensao.dataFim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const inicio = new Date(novaSuspensao.dataInicio + 'T12:00:00');
    const fim = new Date(novaSuspensao.dataFim + 'T12:00:00');

    if (fim <= inicio) {
      toast.error('A data de fim deve ser posterior à data de início');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, `artifacts/${appId}/users/${userId}/suspensoes`), {
        ...novaSuspensao,
        dataInicio: inicio,
        dataFim: fim,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setNovaSuspensao({ nome: '', dataInicio: '', dataFim: '', tipo: 'recesso', tribunal: '', observacoes: '', ativo: true });
      toast.success('Suspensão adicionada com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar suspensão:', error);
      toast.error('Erro ao adicionar suspensão');
    } finally {
      setLoading(false);
    }
  };

  // Editar suspensão
  const handleEditSuspensao = async () => {
    if (!editandoSuspensao || !editandoSuspensao.id) return;

    const inicio = new Date(editandoSuspensao.dataInicio + 'T12:00:00');
    const fim = new Date(editandoSuspensao.dataFim + 'T12:00:00');

    if (fim <= inicio) {
      toast.error('A data de fim deve ser posterior à data de início');
      return;
    }

    setLoading(true);
    try {
      const { id, ...dataToUpdate } = editandoSuspensao;
      await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/suspensoes`, id), {
        ...dataToUpdate,
        dataInicio: inicio,
        dataFim: fim,
        updatedAt: serverTimestamp()
      });
      setEditandoSuspensao(null);
      toast.success('Suspensão atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar suspensão:', error);
      toast.error('Erro ao atualizar suspensão');
    } finally {
      setLoading(false);
    }
  };

  const executeConfirmAction = () => {
    if (confirmAction) confirmAction();
    setIsConfirmOpen(false);
    setConfirmAction(null);
  };

  const handleDeleteSuspensao = (suspensaoId) => {
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/suspensoes`, suspensaoId));
        toast.success('Suspensão removida com sucesso!');
      } catch (error) {
        console.error('Erro ao remover suspensão:', error);
        toast.error('Erro ao remover suspensão');
      } finally {
        setLoading(false);
      }
    });
    setIsConfirmOpen(true);
  };

  const adicionarSuspensaoPredefinida = (predefinida) => {
    setNovaSuspensao({ ...predefinida, ativo: true });
  };

  const calcularDiasSuspensao = (inicio, fim) => {
    if (!inicio || !fim) return 0;
    const diffTime = Math.abs(fim - inicio);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const isSuspensaoAtiva = (suspensao) => {
    const hoje = new Date();
    if (!suspensao.dataInicio || !suspensao.dataFim) return false;
    return hoje >= suspensao.dataInicio && hoje <= suspensao.dataFim && suspensao.ativo;
  };

  const suspensoesOrdenadas = useMemo(() => {
    return suspensoes
      .filter(suspensao => {
        if (!suspensao.dataInicio) return false;
        const anoInicio = suspensao.dataInicio.getFullYear();
        const anoFim = suspensao.dataFim ? suspensao.dataFim.getFullYear() : anoInicio;
        if (anoInicio > filtroAno || anoFim < filtroAno) return false;
        if (filtroTipo !== 'todos' && suspensao.tipo !== filtroTipo) return false;
        return true;
      })
      .sort((a, b) => a.dataInicio - b.dataInicio);
  }, [suspensoes, filtroAno, filtroTipo]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-5/6 overflow-hidden flex flex-col">
        <div className="p-6 border-b bg-purple-50">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <ClockIcon className="h-8 w-8 text-purple-600" /> Gerenciar Suspensões de Prazo
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg">×</button>
          </div>
          <div className="mt-2 text-sm text-gray-600">Configure recessos, suspensões extraordinárias e períodos por tribunal.</div>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
            <div className="p-6 border-r overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">{editandoSuspensao ? 'Editar Suspensão' : 'Adicionar Nova Suspensão'}</h3>
              {!editandoSuspensao && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-3">Suspensões Predefinidas</h4>
                  <div className="space-y-2">
                    {SUSPENSOES_PREDEFINIDAS.map((predefinida, index) => (
                      <button key={index} onClick={() => adicionarSuspensaoPredefinida(predefinida)} className="w-full text-left p-2 bg-white border border-blue-200 rounded text-sm hover:bg-blue-50">
                        <div className="font-medium text-blue-700">{predefinida.nome}</div>
                        <div className="text-xs text-blue-600">
                          {new Date(predefinida.dataInicio + 'T12:00:00').toLocaleDateString('pt-BR')} até {new Date(predefinida.dataFim + 'T12:00:00').toLocaleDateString('pt-BR')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Suspensão *</label>
                  <input type="text" value={editandoSuspensao ? editandoSuspensao.nome : novaSuspensao.nome} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, nome: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, nome: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Ex: Recesso Forense 2024" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início *</label>
                    <input type="date" value={editandoSuspensao ? editandoSuspensao.dataInicio : novaSuspensao.dataInicio} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, dataInicio: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, dataInicio: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Fim *</label>
                    <input type="date" value={editandoSuspensao ? editandoSuspensao.dataFim : novaSuspensao.dataFim} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, dataFim: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, dataFim: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={editandoSuspensao ? editandoSuspensao.tipo : novaSuspensao.tipo} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, tipo: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, tipo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="recesso">Recesso Forense</option>
                    <option value="extraordinaria">Suspensão Extraordinária</option>
                    <option value="tribunal">Específica do Tribunal</option>
                    <option value="personalizada">Personalizada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tribunal/Órgão</label>
                  <input type="text" value={editandoSuspensao ? editandoSuspensao.tribunal : novaSuspensao.tribunal} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, tribunal: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, tribunal: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Ex: TJSP, STJ, etc." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea value={editandoSuspensao ? editandoSuspensao.observacoes : novaSuspensao.observacoes} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, observacoes: e.target.value }) : setNovaSuspensao({ ...novaSuspensao, observacoes: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Observações adicionais..." />
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="ativo" checked={editandoSuspensao ? editandoSuspensao.ativo : novaSuspensao.ativo} onChange={(e) => editandoSuspensao ? setEditandoSuspensao({ ...editandoSuspensao, ativo: e.target.checked }) : setNovaSuspensao({ ...novaSuspensao, ativo: e.target.checked })} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" />
                  <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">Suspensão ativa</label>
                </div>
                <div className="flex gap-2 pt-4">
                  <button type="submit" disabled={loading} className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2">
                    {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <PlusIcon className="h-4 w-4" />}
                    {editandoSuspensao ? 'Atualizar' : 'Adicionar'}
                  </button>
                  {editandoSuspensao && (<button type="button" onClick={() => setEditandoSuspensao(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>)}
                </div>
              </form>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="mb-4 space-y-2">
                <div className="flex gap-2">
                  <select value={filtroAno} onChange={(e) => setFiltroAno(parseInt(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {Array.from({ length: 5 }, (_, i) => { const ano = new Date().getFullYear() + i - 2; return (<option key={ano} value={ano}>{ano}</option>); })}
                  </select>
                  <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="todos">Todos os tipos</option>
                    <option value="recesso">Recesso Forense</option>
                    <option value="extraordinaria">Extraordinária</option>
                    <option value="tribunal">Tribunal</option>
                    <option value="personalizada">Personalizada</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">{suspensoesOrdenadas.length} suspensões em {filtroAno}</div>
              </div>
              <div className="space-y-3">
                {suspensoesOrdenadas.map((suspensao) => (
                  <div key={suspensao.id} className={`p-4 rounded-lg border ${isSuspensaoAtiva(suspensao) ? 'bg-red-50 border-red-200' : suspensao.ativo ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'} hover:bg-gray-50`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-800">{suspensao.nome}</div>
                          {isSuspensaoAtiva(suspensao) && (<span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"><ExclamationTriangleIcon className="h-3 w-3" />Ativa</span>)}
                        </div>
                        {suspensao.dataInicio && suspensao.dataFim && <div className="text-sm text-gray-600 mt-1">{suspensao.dataInicio.toLocaleDateString('pt-BR')} até {suspensao.dataFim.toLocaleDateString('pt-BR')} <span className="ml-2 text-gray-500">({calcularDiasSuspensao(suspensao.dataInicio, suspensao.dataFim)} dias)</span></div>}
                        <div className="flex items-center gap-3 mt-2"><span className={`inline-block px-2 py-1 rounded text-xs ${suspensao.tipo === 'recesso' ? 'bg-purple-100 text-purple-800' : suspensao.tipo === 'extraordinaria' ? 'bg-red-100 text-red-800' : suspensao.tipo === 'tribunal' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{suspensao.tipo.charAt(0).toUpperCase() + suspensao.tipo.slice(1)}</span>{suspensao.tribunal && (<span className="text-xs text-gray-500">{suspensao.tribunal}</span>)}</div>
                        {suspensao.observacoes && (<div className="text-xs text-gray-500 mt-2">{suspensao.observacoes}</div>)}
                      </div>
                      <div className="flex gap-1 ml-3">
                        <button onClick={() => handleOpenEdit(suspensao)} className="p-2 text-gray-400 hover:text-blue-600"><PencilIcon className="h-4 w-4" /></button>
                        <button onClick={() => handleDeleteSuspensao(suspensao.id)} className="p-2 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {suspensoesOrdenadas.length === 0 && (<div className="text-center text-gray-500 py-8"><ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p>Nenhuma suspensão encontrada para {filtroAno}</p><p className="text-sm mt-2">Use as suspensões predefinidas ou crie uma personalizada</p></div>)}
              </div>
            </div>
          </div>
        </div>
        {isConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Confirmar Ação</h3>
              <p className="text-gray-600 mb-6">Tem certeza de que deseja realizar esta ação?</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setIsConfirmOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancelar</button>
                <button onClick={executeConfirmAction} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Confirmar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuspensoesPrazo;